// Microsoft Clarity (heatmaps + session recordings), project "PREP TRACKING".
// The project id is public (it is visible in any page's source), so it ships as
// the default; VITE_CLARITY_ID overrides it, and setting it to an empty string
// switches Clarity off (used by tests and previews).
//
// Recording covers the public pages and the signup funnel (/get-started,
// /register and the onboarding steps), so we can see where owners drop off.
// Everything a visitor types is masked on every page (see maskInputs). Login,
// password reset, billing, the POS-token step and the logged-in app show
// credentials, POS tokens and merchant/customer data, so Clarity is paused
// (clarity("stop")) on those routes and resumed on the next recorded page.
type ClarityFn = (...args: unknown[]) => void;

declare global {
  interface Window {
    clarity?: ClarityFn & { q?: unknown[] };
  }
}

const SCRIPT_ID = "clarity-script";

const PRIVATE_PREFIXES = [
  "/app",
  "/auth",
  "/billing",
  "/forgot-password",
  "/login",
  "/m/",
  "/onboarding/step/pos",
  "/portal",
  "/reset-password",
];

// Typed values never reach a recording: every field gets data-clarity-mask,
// including fields added later by React.
const FIELD_SELECTOR = "input, textarea, select, [contenteditable]";
let maskObserver: MutationObserver | null = null;

function maskFields(root: ParentNode) {
  root.querySelectorAll(FIELD_SELECTOR).forEach((el) => {
    if (el.getAttribute("data-clarity-mask") !== "True") el.setAttribute("data-clarity-mask", "True");
  });
}

export function maskInputs(): void {
  maskFields(document);
  if (maskObserver || typeof MutationObserver === "undefined") return;
  maskObserver = new MutationObserver((records) => {
    for (const r of records) {
      r.addedNodes.forEach((n) => {
        if (!(n instanceof Element)) return;
        if (n.matches(FIELD_SELECTOR)) n.setAttribute("data-clarity-mask", "True");
        maskFields(n);
      });
    }
  });
  maskObserver.observe(document.body, { childList: true, subtree: true });
}

export function isPrivatePath(pathname: string): boolean {
  return PRIVATE_PREFIXES.some((p) => pathname === p || pathname.startsWith(p.endsWith("/") ? p : `${p}/`));
}

const DEFAULT_PROJECT_ID = "ywqhhuokpk";

function projectId(): string | undefined {
  const id = (import.meta.env.VITE_CLARITY_ID as string | undefined) ?? DEFAULT_PROJECT_ID;
  return id && /^[a-z0-9]{6,20}$/i.test(id.trim()) ? id.trim() : undefined;
}

function ensureScript(id: string) {
  if (document.getElementById(SCRIPT_ID)) return;
  // Queue calls made before the script finishes loading.
  window.clarity =
    window.clarity ||
    function (...args: unknown[]) {
      (window.clarity!.q = window.clarity!.q || []).push(args);
    };
  const s = document.createElement("script");
  s.id = SCRIPT_ID;
  s.async = true;
  s.src = `https://www.clarity.ms/tag/${id}`;
  document.head.appendChild(s);
}

let paused = false;

/** Call on every route change: records public and funnel pages, pauses on private ones. */
export function syncClarity(pathname: string): void {
  try {
    const id = projectId();
    if (!id) return;
    if (isPrivatePath(pathname)) {
      // Belt and braces: even if a recording were still running, Clarity masks
      // everything inside an element marked data-clarity-mask.
      document.body.setAttribute("data-clarity-mask", "True");
      if (window.clarity && !paused) {
        window.clarity("stop");
        paused = true;
      }
      return;
    }
    document.body.removeAttribute("data-clarity-mask");
    maskInputs();
    ensureScript(id);
    if (paused) {
      window.clarity?.("start");
      paused = false;
    }
  } catch {
    // tracking must never break the page
  }
}

/** Add a filterable tag to the current recording (e.g. email_campaign). */
export function clarityTag(key: string, value: string): void {
  try {
    if (!projectId() || !window.clarity || paused) return;
    window.clarity("set", key, value);
  } catch {
    // ignore
  }
}

/** Mark a moment in the recording (e.g. trial_click) so it can be filtered. */
export function clarityEvent(name: string): void {
  try {
    if (!projectId() || !window.clarity || paused) return;
    window.clarity("event", name);
  } catch {
    // ignore
  }
}
